import { createContext, useContext, useState, type ReactNode } from 'react';
import type { WorkflowDefinition, Action, Trigger, Position } from '@/types/workflowDefinition';

type WorkflowContextValue = {
  workflow: WorkflowDefinition | null;
  updateWorkflow: (workflow: WorkflowDefinition) => void;
  getAction: (name: string) => Action | undefined;
  getTrigger: (name: string) => Trigger | undefined;
  updateAction: (name: string, action: Action) => void;
  updateActionPosition: (name: string, position: Position) => void;
  updateTriggerPosition: (name: string, position: Position) => void;
};

const WorkflowContext = createContext<WorkflowContextValue | undefined>(undefined);

type WorkflowProviderProps = {
  children: ReactNode;
  initialWorkflow?: WorkflowDefinition;
};

export const WorkflowProvider = ({ children, initialWorkflow }: WorkflowProviderProps) => {
  const [workflow, setWorkflow] = useState<WorkflowDefinition | null>(initialWorkflow ?? null);

  const updateWorkflow = (newWorkflow: WorkflowDefinition): void => {
    setWorkflow(newWorkflow);
  };

  const getAction = (name: string): Action | undefined => {
    if (!workflow) {
      return undefined;
    }
    return workflow.definition.actions[name];
  };

  const getTrigger = (name: string): Trigger | undefined => {
    if (!workflow) {
      return undefined;
    }
    return workflow.definition.triggers[name];
  };

  const updateAction = (name: string, action: Action): void => {
    if (!workflow) {
      return;
    }

    setWorkflow({
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: {
          ...workflow.definition.actions,
          [name]: action,
        },
      },
    });
  };

  const updateActionPosition = (name: string, position: Position): void => {
    if (!workflow) {
      return;
    }

    const existingAction = workflow.definition.actions[name];
    if (!existingAction) {
      return;
    }

    setWorkflow({
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: {
          ...workflow.definition.actions,
          [name]: {
            ...existingAction,
            metadata: {
              ...existingAction.metadata,
              position,
            },
          },
        },
      },
    });
  };

  const updateTriggerPosition = (name: string, position: Position): void => {
    if (!workflow) {
      return;
    }

    const existingTrigger = workflow.definition.triggers[name];
    if (!existingTrigger) {
      return;
    }

    setWorkflow({
      ...workflow,
      definition: {
        ...workflow.definition,
        triggers: {
          ...workflow.definition.triggers,
          [name]: {
            ...existingTrigger,
            metadata: {
              ...existingTrigger.metadata,
              position,
            },
          },
        },
      },
    });
  };

  const value: WorkflowContextValue = {
    workflow,
    updateWorkflow,
    getAction,
    getTrigger,
    updateAction,
    updateActionPosition,
    updateTriggerPosition,
  };

  return <WorkflowContext.Provider value={value}>{children}</WorkflowContext.Provider>;
};

export const useWorkflow = (): WorkflowContextValue => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};
